import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Video from '../../app/LegacyStorywind/Video/Video';
import StockItemIcons from '../../app/Search/components/card/StockItemIcons';
import { posterPlaceholderDataUri } from '../../common/Constants';

import './VideoStockItemPreview.less';

interface Props {
  previewUrl: string;
  shouldStockItemHaveNewFlag: boolean;
  mediumImageUrl?: string;
  thumbnailUrl?: string;
  title?: string;
  className?: string;
  // String taking the form `"<int> x <int>"`
  resolution?: string;
  isVertical?: boolean;
}

/**
 * Convert a resolution string (e.g. `"1920 x 1080"`) into a percentage.
 * The percentage represents the ratio of height to width.
 * @param resolution
 */
export function resolutionStringToPercentage(
  resolution: string,
  isVertical: boolean
): number {
  const [, width, height] = /^(\d+)[ x]+(\d+)$/.exec(resolution) || [
    null,
    isVertical ? 9 : 16,
    isVertical ? 16 : 9,
  ];
  let percentage: number;

  if (isVertical) {
    percentage = (Number(width) / Number(height)) * 100;
  } else {
    percentage = (Number(height) / Number(width)) * 100;
  }
  return percentage;
}

const VideoStockItemPreview = ({
  previewUrl,
  shouldStockItemHaveNewFlag,
  mediumImageUrl,
  thumbnailUrl,
  title,
  className,
  resolution,
  isVertical,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [poster, setPoster] = useState(posterPlaceholderDataUri);

  // The inline placeholder lets LCP fire before any network fetch. The real
  // thumbnail is only worth downloading once the play attempt fails. Any
  // rejection qualifies, not just a blocked autoplay (iOS Low Power Mode,
  // Safari/Firefox autoplay settings): a pause before the first frame or a
  // source that fails to load also leaves the placeholder on screen, and a
  // thumbnail beats gray in every one of those cases.
  useEffect(() => {
    const thumbnail = mediumImageUrl || thumbnailUrl;
    if (!thumbnail) return undefined;

    const playAttempt = videoRef.current?.play();
    if (!playAttempt) return undefined;

    let unmounted = false;
    playAttempt.catch(() => {
      if (!unmounted) setPoster(thumbnail);
    });
    return () => {
      unmounted = true;
    };
  }, [mediumImageUrl, thumbnailUrl]);

  return (
    <div
      className={twMerge('VideoStockItemPreview', 'relative', className)}
      role="region"
      aria-label={title}
      itemType="schema.org/VideoObject"
    >
      <div
        style={{
          paddingTop: `${resolutionStringToPercentage(
            resolution,
            isVertical
          )}%`,
        }}
      />
      <StockItemIcons showNewFlag={shouldStockItemHaveNewFlag} />
      <div className="absolute inset-0 w-full h-full overflow-hidden rounded-t-xl xl:rounded-tr-none">
        <Video
          ref={videoRef}
          className="w-full h-full"
          videoUrl={previewUrl}
          showControls
          autoPlay
          playsInline
          poster={poster}
        />
      </div>
    </div>
  );
};

export default VideoStockItemPreview;
